'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Hl, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/calculus'

const LossSurface = dynamic(() => import('../_components/widgets/three/LossSurface'), { ssr: false })
const GradientDescent1D = dynamic(() => import('../_components/widgets/CalcWidgets').then(m => m.GradientDescent1D), { ssr: false })
const StepAnatomy = dynamic(() => import('../_components/widgets/CalcWidgets').then(m => m.StepAnatomy), { ssr: false })

export default function Gradients() {
  return (
    <Lesson>
      <Step>
        <H>Going downhill, one step at a time</H>
        <P>
          Here is gradient descent in one dimension. The curve is a loss: how wrong a model is for each value of its parameter{' '}
          <M>{'\\green{w}'}</M>. We start somewhere, look at the slope, and take a step <strong>against</strong> it — downhill.
        </P>
        <MB>{'\\green{w} \\leftarrow \\green{w} - \\eta \\cdot \\pink{\\frac{dL}{dw}}'}</MB>
        <Callout kind="notation">
          <M>{'\\leftarrow'}</M> means “gets replaced by”. <M>{'\\eta'}</M> (the Greek letter eta) is the{' '}
          <strong>learning rate</strong>: how big a step to take. The minus sign is the whole trick — slope positive means
          uphill is to the right, so go left.
        </Callout>
        <Figure n="4.6" caption="The anatomy of one step. Drag the point; the green arrow is where one step of gradient descent lands.">
          <StepAnatomy />
        </Figure>
      </Step>

      <Step gate="q-lr">
        <H>The learning rate matters</H>
        <Figure n="4.7" caption="Run gradient descent from different starting points and learning rates. Too small: painfully slow. Too large: it overshoots and bounces — or flies off entirely.">
          <GradientDescent1D />
        </Figure>
        <Quiz
          id="q-lr"
          question="You set the learning rate very high and the loss starts going UP each step. What’s happening?"
          options={[
            { label: 'Each step overshoots the valley and lands higher up the other side.', correct: true },
            { label: 'The gradient is pointing the wrong way.', why: 'The direction is right — the step is just far too long.' },
            { label: 'The model has finished learning.', why: 'A rising loss is the opposite of finished.' },
          ]}
          explain="Picking the learning rate is one of the most important practical decisions in training. Notice also that the bumpy curve has more than one valley: where you start can decide which valley you end up in."
        />
      </Step>

      <Step>
        <H>Two inputs: a landscape</H>
        <P>
          Most functions in machine learning have many inputs. With two, <M>{'f(x, y)'}</M> is a surface — a landscape where the
          height is the loss. Now there isn’t one slope, there are two: the slope if you walk in the <Hl c="green">x</Hl>{' '}
          direction, and the slope if you walk in the <Hl c="amber">y</Hl> direction. These are{' '}
          <strong>partial derivatives</strong>, written with a curly ∂:
        </P>
        <MB>{'\\green{\\frac{\\partial f}{\\partial x}} \\quad \\text{and} \\quad \\amber{\\frac{\\partial f}{\\partial y}}'}</MB>
        <P>To find one, you wiggle one input and hold all the others still — exactly like slicing the landscape along one direction.</P>
        <Figure n="4.8" caption="A surface with two slices through the ball: the green slice is ‘walk along x’, the amber one ‘walk along y’. Their slopes are the two partial derivatives.">
          <LossSurface mode="slices" />
        </Figure>
        <Exercise id="partials" title="Partial derivatives, numerically" {...EX.partials} />
      </Step>

      <Step gate="q-grad">
        <H>The gradient points uphill</H>
        <P>Put the partial derivatives together into a vector and you get the <strong>gradient</strong>, written with an upside-down triangle (“nabla”):</P>
        <MB>{'\\pink{\\nabla f} = \\left[\\green{\\frac{\\partial f}{\\partial x}},\\; \\amber{\\frac{\\partial f}{\\partial y}}\\right]'}</MB>
        <P>
          The gradient is an arrow that points in the direction of <strong>steepest climb</strong>. Flip it — <M>{'-\\nabla f'}</M>{' '}
          — and you have the direction of steepest <em>descent</em>. Gradient descent in many dimensions is the same rule as
          before, applied to every coordinate at once:
        </P>
        <MB>{'\\green{\\mathbf{w}} \\leftarrow \\green{\\mathbf{w}} - \\eta\\, \\pink{\\nabla L(\\mathbf{w})}'}</MB>
        <Figure n="4.9" caption="Gradient descent on a surface. Try the long valley with a large learning rate (it zig-zags), and the bumpy landscape from different random starts (different valleys).">
          <LossSurface mode="descent" />
        </Figure>
        <Quiz
          id="q-grad"
          question={<>At some point, <M>{'\\nabla f = [3, -1]'}</M>. Which direction should gradient descent move?</>}
          options={[
            { label: <M>{'[-3, 1]'}</M>, correct: true },
            { label: <M>{'[3, -1]'}</M>, why: 'That’s uphill — the gradient points toward the steepest climb.' },
            { label: <M>{'[0, 0]'}</M>, why: 'Only stand still when the gradient itself is zero (at the bottom).' },
          ]}
          explain="Go against the gradient: decrease x (its partial is positive) and increase y (its partial is negative)."
        />
      </Step>

      <Step gate="descend">
        <H>Write gradient descent</H>
        <Exercise
          id="descend"
          title="Find the bottom of a bowl"
          {...EX.descend}
          hints={[
            <>
              Inside a <C>{'for (let step = 0; step < 100; step++)'}</C> loop, get <C>const [gx, gy] = gradient(x, y)</C>.
            </>,
            <>
              Then update both: <C>x = x - lr * gx</C> and <C>y = y - lr * gy</C>.
            </>,
          ]}
        />
        <Exercise id="descend-plot" title="Watch the loss fall" {...EX.descendPlot} />
      </Step>

      <Step>
        <H>That’s training</H>
        <P>
          Replace “two inputs” with “every weight in a neural network” — millions or billions of them — and the landscape with
          “how wrong the model is on the training data”. Compute the gradient (with the chain rule, via backpropagation), take a
          small step downhill, repeat. That’s it. That is how every model you’ve heard of was trained.
        </P>
        <Callout kind="why">
          You now have all three mathematical pillars of deep learning: <strong>vectors and matrices</strong> to represent
          everything, <strong>derivatives</strong> to know which way is downhill, and <strong>gradient descent</strong> to get
          there. One more ingredient remains: how a model can express <em>uncertainty</em>. That’s probability.
        </Callout>
      </Step>
    </Lesson>
  )
}
